import React from 'react';
import {
  AvatarGroup, BottomNavigation, BottomNavigationAction, Box, Button, ButtonBase, Paper, Tab, Tabs, Typography,
  useMediaQuery, useTheme,
} from '@mui/material';
import CalendarViewWeekIcon from '@mui/icons-material/CalendarViewWeek';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import MicIcon from '@mui/icons-material/Mic';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import TvOutlinedIcon from '@mui/icons-material/TvOutlined';
import EventNoteIcon from '@mui/icons-material/EventNote';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import KitchenOutlinedIcon from '@mui/icons-material/KitchenOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import ListAltIcon from '@mui/icons-material/ListAlt';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import RepeatIcon from '@mui/icons-material/Repeat';
import TuneIcon from '@mui/icons-material/Tune';
import EuroIcon from '@mui/icons-material/Euro';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { HouseholdProvider, MemberAvatar, NotifyProvider, useHousehold } from './common';
import { APP_BAR_HEIGHT } from '../../theme';
import { CONTENT_MAX_WIDTH, mainSx, sidebarSx } from '../../components/layout/SectionLayout';
import { currentLink, SideLink, SideNavItem, SideNavSections, SideSection } from '../../components/layout/SideNav';

interface NavPage { label: string; path: string; }
interface NavGroup { label: string; icon: React.ReactElement<{ fontSize?: string }>; pages: NavPage[]; }

/** Same grouping as the Android tabs: daily use in four tabs, the voice in the middle, the rest in "Mais". */
const NAV: NavGroup[] = [
  { label: 'Semana', icon: <CalendarViewWeekIcon />, pages: [{ label: 'Visão', path: '/casa/semana' }, { label: 'Tarefas', path: '/casa/tarefas' }] },
  {
    label: 'Comida', icon: <RestaurantIcon />,
    pages: [{ label: 'Plano', path: '/casa/refeicoes' }, { label: 'Receitas', path: '/casa/receitas' }, { label: 'Congelador', path: '/casa/congelador' }],
  },
  { label: 'Dizer', icon: <MicIcon />, pages: [{ label: 'Dizer', path: '/casa/dizer' }] },
  {
    label: 'Compras', icon: <ShoppingCartIcon />,
    pages: [{ label: 'Lista', path: '/casa/compras' }, { label: 'Em casa', path: '/casa/em-casa' }, { label: 'Idas', path: '/casa/idas' }],
  },
  {
    label: 'Mais', icon: <MoreHorizIcon />,
    pages: [
      { label: 'Mais', path: '/casa/mais' },
      { label: 'Casa e pessoas', path: '/casa/agregado' },
      { label: 'Catálogo', path: '/casa/catalogo' },
    ],
  },
];

/**
 * The desktop sidebar: everything one click away, so no "Mais" page in between.
 * The voice gets its own button above the groups.
 */
const SIDEBAR: SideSection[] = [
  {
    label: 'Semana',
    links: [
      { label: 'Visão geral', path: '/casa/semana', icon: <DashboardOutlinedIcon /> },
      { label: 'Tarefas', path: '/casa/tarefas', icon: <TaskAltIcon /> },
      { label: 'Painel da casa', path: '/casa/painel', icon: <TvOutlinedIcon /> },
    ],
  },
  {
    label: 'Comida',
    links: [
      { label: 'Plano de refeições', path: '/casa/refeicoes', icon: <EventNoteIcon /> },
      { label: 'Receitas', path: '/casa/receitas', icon: <MenuBookOutlinedIcon /> },
      { label: 'Congelador', path: '/casa/congelador', icon: <AcUnitIcon /> },
    ],
  },
  {
    label: 'Compras',
    links: [
      { label: 'Lista de compras', path: '/casa/compras', icon: <ShoppingCartOutlinedIcon /> },
      { label: 'Em casa', path: '/casa/em-casa', icon: <KitchenOutlinedIcon /> },
      { label: 'Idas às compras', path: '/casa/idas', icon: <LocalShippingOutlinedIcon /> },
    ],
  },
  {
    label: 'Organizar',
    links: [
      { label: 'Casa e pessoas', path: '/casa/agregado', icon: <PeopleOutlineIcon /> },
      { label: 'Catálogo de artigos', path: '/casa/catalogo', icon: <ListAltIcon /> },
      { label: 'Lojas', path: '/casa/compras/lojas', icon: <StorefrontOutlinedIcon /> },
      { label: 'Compras regulares', path: '/casa/compras/regulares', icon: <EventRepeatIcon /> },
      { label: 'Locais e zonas', path: '/casa/em-casa/locais', icon: <PlaceOutlinedIcon /> },
      { label: 'Padrões das refeições', path: '/casa/refeicoes/padroes', icon: <RepeatIcon /> },
      { label: 'Regras das sugestões', path: '/casa/refeicoes/regras', icon: <TuneIcon /> },
    ],
  },
];

const matches = (pathname: string, path: string) => pathname === path || pathname.startsWith(path + '/');

/** The group and page that own the current path (longest matching prefix). */
function current(pathname: string): { group: number; page: string | null } {
  let best = { group: 0, page: null as string | null, length: 0 };
  NAV.forEach((group, index) => group.pages.forEach(page => {
    if (matches(pathname, page.path) && page.path.length > best.length)
      best = { group: index, page: page.path, length: page.path.length };
  }));
  // Pages reached from "Mais" (stores, locations, patterns, ...) live under other areas' paths
  if (/^\/casa\/(compras\/(lojas|regulares)|em-casa\/locais|refeicoes\/(padroes|regras))/.test(pathname)) return { group: 4, page: null };
  return best;
}

const HOME_EXPENSES: SideLink = { label: 'Despesas da casa', path: '/expenses/home-expenses', icon: <EuroIcon /> };

function Sidebar() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { household, members } = useHousehold();
  const selected = currentLink(SIDEBAR, pathname);
  const voice = matches(pathname, '/casa/dizer');

  return (
    <Box component="nav" aria-label="Casa" sx={sidebarSx}>
      <ButtonBase
        onClick={() => navigate('/casa/agregado')}
        sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 1.5, p: 1, mb: 1.5, borderRadius: 3, textAlign: 'left', '&:hover': { bgcolor: 'action.hover' } }}
      >
        <Box sx={{ width: 40, height: 40, borderRadius: 2.5, bgcolor: 'primary.main', color: '#fff', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
          <HomeOutlinedIcon fontSize="small" />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, lineHeight: 1.25 }} noWrap>{household?.name ?? 'Casa'}</Typography>
          <Typography variant="caption" color="text.secondary" noWrap component="div">
            {members.length === 0 ? 'Planear a casa' : members.length === 1 ? '1 pessoa' : `${members.length} pessoas`}
          </Typography>
        </Box>
        {members.length > 0 && (
          <AvatarGroup max={3} sx={{ ml: 'auto', '& .MuiAvatar-root': { width: 24, height: 24, fontSize: 11, borderWidth: 1.5 } }}>
            {members.map(m => <MemberAvatar key={m.publicId} member={m} size={24} />)}
          </AvatarGroup>
        )}
      </ButtonBase>

      <Button
        variant={voice ? 'contained' : 'outlined'}
        startIcon={<MicIcon />}
        onClick={() => navigate('/casa/dizer')}
        sx={{ mb: 2, py: 1, justifyContent: 'flex-start', borderStyle: 'dashed', '&.MuiButton-contained': { borderStyle: 'solid' } }}
      >
        Dizer o que queres
      </Button>

      <SideNavSections sections={SIDEBAR} selected={selected} onNavigate={navigate} />

      <Box sx={{ mt: 'auto', pt: 1, borderTop: 1, borderColor: 'divider' }}>
        <SideNavItem
          link={HOME_EXPENSES}
          selected={false}
          onClick={() => navigate('/expenses/home-expenses')}
        />
      </Box>
    </Box>
  );
}

const PlannerShell: React.FC = () => {
  const isMobile = useMediaQuery(useTheme().breakpoints.down('md'));
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { group, page } = current(pathname);
  const subPages = NAV[group].label === 'Mais' ? [] : NAV[group].pages;

  return (
    <>
      <Box sx={{ display: 'flex', bgcolor: 'background.default', minHeight: `calc(100vh - ${APP_BAR_HEIGHT}px)`, color: 'text.primary' }}>
        {!isMobile && <Sidebar />}
        {/* Room at the bottom on phones for the tab bar */}
        <Box component="main" sx={{ ...mainSx, px: { xs: 1.5, sm: 2.5, lg: 4 }, pb: { xs: 11, md: 6 } }}>
          <Box sx={{ maxWidth: CONTENT_MAX_WIDTH, mx: 'auto' }}>
            {isMobile && subPages.length > 1 && page && (
              <Tabs value={page} onChange={(_, value) => navigate(value)} variant="fullWidth" sx={{ mb: 1.5, mt: -1 }}>
                {subPages.map(p => <Tab key={p.path} value={p.path} label={p.label} />)}
              </Tabs>
            )}
            <Outlet />
          </Box>
        </Box>
      </Box>
      {isMobile && (
        <Paper elevation={8} sx={{ position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: t => t.zIndex.appBar, borderRadius: 0 }}>
          <BottomNavigation showLabels value={group} onChange={(_, index: number) => navigate(NAV[index].pages[0].path)}>
            {NAV.map(g => (
              <BottomNavigationAction key={g.label} label={g.label} icon={g.icon} />
            ))}
          </BottomNavigation>
        </Paper>
      )}
    </>
  );
};

const PlannerLayout: React.FC = () => (
  <HouseholdProvider>
    <NotifyProvider>
      <PlannerShell />
    </NotifyProvider>
  </HouseholdProvider>
);

export default PlannerLayout;
